import * as React from "react";
import PropTypes from "prop-types";
import { styled } from "@material-ui/core/styles";
import { Dialog, CircularProgress, Grid, Typography } from "@material-ui/core";
import currencyFormatter from "../../util/formatCurrency";
import StripeProvider from "../../services/StripeProvider";
import usePurchases from "../../hooks/usePurchases";
import { scrollbarStyles, DialogTitle } from "../../theme";
import PurchaseForm from "./PurchaseForm";
import { cancelPaymentIntent } from "./service";

export default function PurchaseDialog({ purchase, open, onClose, content }) {
  const [purchaseComplete, setPurchaseComplete] = React.useState(false);
  const [purchasePending, setPurchasePending] = React.useState(false);
  const { purchaseDetails, isLoading, isError } = usePurchases(purchase);

  const wrappedOnClose = React.useCallback(() => {
    cancelPaymentIntent(purchaseDetails?.stripe ?? {});
    onClose();
  }, [onClose, purchaseDetails]);

  return (
    <StyledDialog
      open={open}
      aria-labelledby="addon-purchase-form-title"
      keepMounted={false}
    >
      <DialogTitle
        id="addon-purchase-form-title"
        onClose={purchaseComplete ? onClose : wrappedOnClose}
        subtitle={purchase.description}
        disabled={purchasePending}
      >
        Purchase {purchaseComplete ? "Complete" : purchase.title}
      </DialogTitle>

      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Typography component="p" variant="h5">
          {currencyFormatter.format(
            (purchaseDetails?.total ?? purchase.amount) / 100,
          )}
        </Typography>

        {purchaseDetails && (
          <Typography component="p">
            <Typography component="span" variant="caption">
              {currencyFormatter.format(purchaseDetails.amount / 100)}{" "}
              {purchase.title}
            </Typography>

            <Typography component="span" variant="caption">
              {" "}
              + {currencyFormatter.format(purchaseDetails.tax / 100)} Tax
            </Typography>

            {purchaseDetails.shipping_cost > 0 && (
              <Typography component="span" variant="caption">
                {" "}
                +{" "}
                {currencyFormatter.format(
                  purchaseDetails.shipping_cost / 100,
                )}{" "}
                Shipping
              </Typography>
            )}
          </Typography>
        )}
      </Grid>

      {Boolean(content) &&
        content({
          purchaseComplete,
          purchasePending,
          purchaseDetails,
          isLoading,
          isError,
        })}

      {isLoading && loadingUI}

      {isError && errorUI}

      {!isLoading && !isError && (
        <StripeProvider clientSecret={purchaseDetails?.stripe?.clientSecret}>
          <PurchaseForm
            purchaseDetails={purchaseDetails}
            onSubmit={onClose}
            onClose={wrappedOnClose}
            showBillingAddress={Boolean(purchase.billingAddress)}
            showShippingAddress={Boolean(purchase.shippingAddress)}
            purchaseComplete={purchaseComplete}
            setPurchaseComplete={setPurchaseComplete}
            purchasePending={purchasePending}
            setPurchasePending={setPurchasePending}
          />
        </StripeProvider>
      )}
    </StyledDialog>
  );
}

PurchaseDialog.propTypes = {
  purchase: PropTypes.oneOfType([
    PropTypes.shape({
      type: PropTypes.oneOf(["addonPurchase"]).isRequired,
      name: PropTypes.string,
      price: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      autographPurchaseId: PropTypes.number,
    }),
  ]).isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  content: PropTypes.func,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: theme.spacing(1),
    minWidth: "33vw",
    minHeight: "18rem",
  },
  "& .MuiDialog-paperScrollPaper": {
    ...scrollbarStyles(theme),
  },
}));

const loadingUI = (
  <Grid container justifyContent="center" style={{ margin: "auto" }}>
    <CircularProgress />
  </Grid>
);

const errorUI = (
  <Grid container justifyContent="center" style={{ margin: "auto" }}>
    <Typography color="error">Error loading purchase details</Typography>
  </Grid>
);
